if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.colorFrame = requestAnimationFrame(() => this.updateColorGroup());
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.colorFrame);
      }

      previewColorGroup(picker) {
        const options = picker?.getAllSelectedOptions?.();
        if (!options) return;
        const find = names => options.find(option => names.includes(option.name.trim().toLowerCase()));
        const color = find(['color', 'colour', '颜色']);
        if (!color) return;
        const device = find(['device', 'model', 'phone model', '型号']);
        this.updateColorGroup(null, { selected: color.value, device: device?.value || '' });
      }

      updateColorGroup(html, previewSelection) {
        const configNode = this.querySelector('[data-color-gallery-config]');
        if (!configNode) return;
        const config = JSON.parse(configNode.textContent);
        const updated = html?.querySelector('[data-color-gallery-config]');
        const normalize = value => String(value || '').trim().toLowerCase();
        const selection = previewSelection || (updated ? JSON.parse(updated.textContent) : config);
        const selected = normalize(selection.selected);
        const device = normalize(selection.device);
        const selectionKey = JSON.stringify([selected, device]);
        if (this.colorSelectionKey === selectionKey) return;
        this.colorSelectionKey = selectionKey;
        this.colorSelection = selected;
        const media = config.media.map(item => {
          const tags = String(item.alt || '').split('#');
          return { ...item, color: normalize(tags[1]), devices: (tags[2] || '').split('|').map(normalize).filter(Boolean) };
        });
        // An explicit color tag is never shared, even if an option was renamed.
        const shared = media.filter(item => !item.color);
        const colorMedia = media.filter(item => item.color === selected);
        const exact = colorMedia.filter(item => item.devices.length && item.devices.includes(device));
        const generic = colorMedia.filter(item => !item.devices.length);
        const matching = [...exact, ...generic];
        // Prefer exact model images, keeping color-only and untagged shared images.
        // Preserve the existing non-empty fallback for combinations without images.
        const group = [...matching, ...shared];
        const allowed = new Set((group.length ? group : media).map(item => item.id));
        const sectionId = this.id.replace('MediaGallery-', '');
        const slides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const visible = slides.filter(slide => {
          const show = allowed.has(slide.dataset.mediaId.slice(sectionId.length + 1));
          slide.toggleAttribute('data-color-filter-hidden', !show);
          if (!show) slide.classList.remove('is-active');
          return show;
        });
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach(thumbnail => {
          thumbnail.toggleAttribute('data-color-filter-hidden', !allowed.has(thumbnail.dataset.target.slice(sectionId.length + 1)));
        });
        const modal = document.getElementById('ProductModal-' + sectionId);
        modal?.querySelectorAll('[data-media-id]').forEach(media => {
          const show = allowed.has(media.dataset.mediaId);
          media.toggleAttribute('data-color-filter-hidden', !show);
          media.toggleAttribute('data-color-filter-visible', show);
          if (!show) media.classList.remove('active');
        });
        if (!visible.length) return;
        // Start at a color-specific image even when shared images precede it.
        const first = visible.find(slide => slide.dataset.mediaId === sectionId + '-' + matching[0]?.id) || visible[0];
        // Fetch the newly selected hero immediately; keep other images lazy on mobile.
        const hero = first.querySelector('.product__modal-opener--image img');
        if (hero) {
          hero.fetchPriority = 'high';
          hero.loading = 'eager';
        }
        this.setActiveMedia(first.dataset.mediaId, false);
        [this.elements.viewer, this.elements.thumbnails].filter(Boolean).forEach(slider => {
          slider.slider?.scrollTo({ left: 0, behavior: 'instant' });
          slider.resetPages?.();
          if (slider.sliderItemsToShow?.length === 1) {
            slider.sliderItemOffset = slider.slider.clientWidth || 1;
            slider.totalPages = 1;
            slider.currentPage = 1;
            if (slider.currentPageElement) slider.currentPageElement.textContent = '1';
            if (slider.pageTotalElement) slider.pageTotalElement.textContent = '1';
            slider.prevButton?.setAttribute('disabled', '');
            slider.nextButton?.setAttribute('disabled', '');
          }
        });
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const rail = this.elements.thumbnails.slider;
        if (rail && getComputedStyle(rail).flexDirection === 'column') {
          const box = thumbnail.getBoundingClientRect();
          const viewport = rail.getBoundingClientRect();
          if (box.top < viewport.top || box.bottom > viewport.bottom) {
            rail.scrollTo({ top: rail.scrollTop + box.top - viewport.top, behavior: 'smooth' });
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
